import { useEffect, useRef, useState, type ChangeEvent, type ReactNode } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { zodResolver } from "@hookform/resolvers/zod";
import { Globe2, ImagePlus, Loader2, PencilLine, RefreshCw, X } from "lucide-react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { extractApiErrorMessage } from "@/lib/api/error";
import { logError, logInfo } from "@/lib/structured-log";
import {
  getWorkspaceCompanyProfileServer,
  getWorkspaceCompanyProfileLogoSignatureServer,
  previewWorkspaceCompanyProfileServer,
  saveWorkspaceCompanyProfileServer,
  type WorkspaceCompanyProfile,
  type WorkspaceCompanyProfileInput,
} from "@/server/workspace-company-profile";

export const Route = createFileRoute("/workspace/$workspaceSlug/knowledge/company-profiles")({
  loader: async ({ parentMatchPromise }) => {
    const parent = await parentMatchPromise;
    const workspaceId = parent?.loaderData?.workspaceId;
    if (!workspaceId || typeof workspaceId !== "string") {
      throw new Error("workspaceId not found in parent loader");
    }

    const profile = await getWorkspaceCompanyProfileServer({
      data: { workspaceId },
    }).catch((error) => {
      logError("workspace_company_profile.page.load_failed", {
        workspace_id: workspaceId,
        error: error instanceof Error ? error.message : String(error),
      });
      return null;
    });

    return { workspaceId, profile };
  },
  component: CompanyProfilesEntryPage,
});

const companyProfileFormSchema = z.object({
  logo: z.string().nullable(),
  name: z.string(),
  website: z.string(),
  location: z.string(),
  foundedIn: z.string(),
  businessType: z.string(),
  description: z.string(),
  targetAudience: z.string(),
  sellingPointsText: z.string(),
});

type CompanyProfileFormValues = z.infer<typeof companyProfileFormSchema>;

const EMPTY_FORM_VALUES: CompanyProfileFormValues = {
  logo: null,
  name: "",
  website: "",
  location: "",
  foundedIn: "",
  businessType: "",
  description: "",
  targetAudience: "",
  sellingPointsText: "",
};

function CompanyProfilesEntryPage() {
  const { workspaceId, profile: initialProfile } = Route.useLoaderData();
  const { t, i18n } = useTranslation(["pages", "common"]);
  const [websiteUrl, setWebsiteUrl] = useState<string>(initialProfile?.website ?? "");
  const [profile, setProfile] = useState<WorkspaceCompanyProfile | null>(initialProfile);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isLogoUploading, setIsLogoUploading] = useState<boolean>(false);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);
  const [showForm, setShowForm] = useState<boolean>(Boolean(initialProfile));
  const [isRegenerateOpen, setIsRegenerateOpen] = useState<boolean>(false);
  const [regenerateUrl, setRegenerateUrl] = useState<string>(initialProfile?.website ?? "");
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const form = useForm<CompanyProfileFormValues>({
    resolver: zodResolver(companyProfileFormSchema),
    defaultValues: initialProfile ? toFormValues(initialProfile) : EMPTY_FORM_VALUES,
  });
  const logoValue = form.watch("logo");
  const logoImageUrl = resolveLogoImageUrl(logoValue, logoPreviewUrl, profile);
  const isLogoControlDisabled = isSaving || isGenerating || isLogoUploading;
  const hasSavedProfile = Boolean(profile?.id);

  useEffect(() => {
    return () => {
      if (logoPreviewUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(logoPreviewUrl);
      }
    };
  }, [logoPreviewUrl]);

  useEffect(() => {
    setProfile(initialProfile);
    setLogoPreviewUrl(null);
    setWebsiteUrl(initialProfile?.website ?? "");
    setRegenerateUrl(initialProfile?.website ?? "");
    setShowForm(Boolean(initialProfile));
    form.reset(initialProfile ? toFormValues(initialProfile) : EMPTY_FORM_VALUES);
  }, [form, initialProfile]);

  const generateCompanyProfile = async (
    website: string,
    options: { closeRegeneratePanel?: boolean } = {},
  ): Promise<void> => {
    const normalizedWebsite = website.trim();
    if (!normalizedWebsite || isGenerating) return;

    setIsGenerating(true);
    try {
      const draft = await previewWorkspaceCompanyProfileServer({
        data: {
          workspaceId,
          website: normalizedWebsite,
          language: i18n.language,
        },
      });
      setProfile(draft);
      setLogoPreviewUrl(null);
      setWebsiteUrl(draft.website);
      setRegenerateUrl(draft.website);
      form.reset(toFormValues(draft));
      setShowForm(true);
      if (options.closeRegeneratePanel) {
        setIsRegenerateOpen(false);
      }
      toast.success(t("pages:knowledge.companyProfiles.generated"));
    } catch (error) {
      logError("workspace_company_profile.page.generate_failed", {
        workspace_id: workspaceId,
        error: error instanceof Error ? error.message : String(error),
      });
      toast.error(t("pages:knowledge.companyProfiles.errors.generateFailed"));
    } finally {
      setIsGenerating(false);
    }
  };

  const handleGenerate = async (): Promise<void> => {
    await generateCompanyProfile(websiteUrl);
  };

  const handleManualEntry = (): void => {
    setProfile(null);
    setLogoPreviewUrl(null);
    form.reset(EMPTY_FORM_VALUES);
    setShowForm(true);
  };

  const handleRegenerate = async (): Promise<void> => {
    await generateCompanyProfile(regenerateUrl, { closeRegeneratePanel: true });
  };

  const handleSave = async (values: CompanyProfileFormValues): Promise<void> => {
    if (isSaving) return;

    setIsSaving(true);
    try {
      const savedProfile = await saveWorkspaceCompanyProfileServer({
        data: {
          workspaceId,
          profile: toSaveInput(values, profile),
        },
      });
      setProfile(savedProfile);
      setLogoPreviewUrl(null);
      form.reset(toFormValues(savedProfile));
      setShowForm(true);
      toast.success(t("pages:knowledge.companyProfiles.saved"));
    } catch (error) {
      logError("workspace_company_profile.page.save_failed", {
        workspace_id: workspaceId,
        error: error instanceof Error ? error.message : String(error),
      });
      toast.error(t("pages:knowledge.companyProfiles.errors.saveFailed"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleRegenerateOpenChange = (open: boolean): void => {
    if (isGenerating) return;
    setIsRegenerateOpen(open);
  };

  const openRegeneratePanel = (): void => {
    const currentWebsite = form.getValues("website").trim() || websiteUrl.trim();
    setRegenerateUrl(currentWebsite);
    setIsRegenerateOpen(true);
  };

  const handleLogoUploadClick = (): void => {
    if (isLogoControlDisabled) return;
    fileInputRef.current?.click();
  };

  const handleLogoFileChange = async (event: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || isLogoUploading) return;

    if (!isValidLogoFile(file)) {
      toast.error(t("pages:knowledge.companyProfiles.logoActions.invalidFile"));
      return;
    }

    setIsLogoUploading(true);
    try {
      logInfo("workspace_company_profile.logo.upload.started", {
        workspace_id: workspaceId,
        filename: file.name,
        file_size: file.size,
      });

      const signature = await getWorkspaceCompanyProfileLogoSignatureServer({
        data: {
          workspaceId,
          filename: file.name,
        },
      });

      await uploadCompanyProfileLogoToOSS(file, signature.upload_url, signature.form_fields);

      const localPreviewUrl = URL.createObjectURL(file);
      setLogoPreviewUrl(localPreviewUrl);
      form.setValue("logo", signature.object_key, {
        shouldDirty: true,
        shouldValidate: true,
      });

      logInfo("workspace_company_profile.logo.upload.completed", {
        workspace_id: workspaceId,
        object_key: signature.object_key,
      });
      toast.success(t("pages:knowledge.companyProfiles.logoActions.uploaded"));
    } catch (error) {
      const message = extractApiErrorMessage(
        error,
        t("pages:knowledge.companyProfiles.logoActions.uploadFailed"),
      );
      logError("workspace_company_profile.logo.upload.failed", {
        workspace_id: workspaceId,
        error: message,
      });
      toast.error(message);
    } finally {
      setIsLogoUploading(false);
    }
  };

  const handleLogoRemove = (): void => {
    if (isLogoControlDisabled || !logoValue) return;
    setLogoPreviewUrl(null);
    form.setValue("logo", null, {
      shouldDirty: true,
      shouldValidate: true,
    });
    toast.success(t("pages:knowledge.companyProfiles.logoActions.removed"));
  };

  if (!showForm) {
    return (
      <div className="h-full overflow-auto bg-white px-6 py-8">
        <div className="mx-auto flex min-h-full w-full max-w-[860px] items-center">
          <div className="w-full">
            <div className="mb-7 text-center">
              <h2 className="text-[20px] font-semibold text-gray-950">
                {t("pages:knowledge.companyProfiles.entryTitle")}
              </h2>
              <p className="mx-auto mt-3 max-w-[560px] text-sm leading-6 text-gray-500">
                {t("pages:knowledge.companyProfiles.entryDescription")}
              </p>
            </div>

            <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_260px]">
              <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
                <div className="mb-4 flex items-start gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gray-950 text-white">
                    <Globe2 className="h-4 w-4" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-950">
                      {t("pages:knowledge.companyProfiles.websiteGenerateTitle")}
                    </h3>
                    <p className="mt-1 text-sm leading-5 text-gray-500">
                      {t("pages:knowledge.companyProfiles.websiteGenerateDescription")}
                    </p>
                  </div>
                </div>
                <label
                  className="mb-2 block text-sm font-medium text-gray-900"
                  htmlFor="company-website"
                >
                  {t("pages:knowledge.companyProfiles.websiteQuestion")}
                </label>
                <Input
                  id="company-website"
                  className="h-11 rounded-lg"
                  placeholder={t("pages:knowledge.companyProfiles.websitePlaceholder")}
                  value={websiteUrl}
                  onChange={(event) => setWebsiteUrl(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      void handleGenerate();
                    }
                  }}
                />
                <div className="mt-4 flex items-center justify-end">
                  <Button
                    type="button"
                    disabled={!websiteUrl.trim() || isGenerating}
                    onClick={() => void handleGenerate()}
                  >
                    {isGenerating ? (
                      <span className="inline-flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        {t("pages:knowledge.companyProfiles.generating")}
                      </span>
                    ) : (
                      <span>{t("pages:knowledge.companyProfiles.generate")}</span>
                    )}
                  </Button>
                </div>
              </section>

              <button
                type="button"
                onClick={handleManualEntry}
                className="rounded-lg border border-gray-200 bg-gray-50 p-5 text-left transition hover:border-gray-300 hover:bg-white"
              >
                <div className="mb-4 flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-700">
                  <PencilLine className="h-4 w-4" />
                </div>
                <h3 className="text-sm font-semibold text-gray-950">
                  {t("pages:knowledge.companyProfiles.manualEntry")}
                </h3>
                <p className="mt-2 text-sm leading-5 text-gray-500">
                  {t("pages:knowledge.companyProfiles.manualEntryDescription")}
                </p>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="h-full overflow-auto bg-white px-6 py-5">
        <form
          className="mx-auto w-full max-w-[1160px] space-y-4 text-sm"
          onSubmit={(event) => {
            event.preventDefault();
            void form.handleSubmit(handleSave)(event);
          }}
        >
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-gray-900">
                {t("pages:knowledge.companyProfiles.fields.logo")}
              </label>
              <input
                ref={fileInputRef}
                type="file"
                accept={ALLOWED_LOGO_TYPES.join(",")}
                className="hidden"
                onChange={(event) => {
                  void handleLogoFileChange(event);
                }}
              />
              <div className="relative h-16 w-16">
                <button
                  type="button"
                  className="peer flex h-16 w-16 items-center justify-center overflow-hidden rounded-lg border border-gray-200 bg-gray-50 transition hover:border-gray-400 disabled:cursor-not-allowed disabled:opacity-70"
                  disabled={isLogoControlDisabled}
                  onClick={handleLogoUploadClick}
                  aria-label={
                    logoValue
                      ? t("pages:knowledge.companyProfiles.logoActions.replace")
                      : t("pages:knowledge.companyProfiles.logoActions.upload")
                  }
                >
                  {logoImageUrl ? (
                    <img
                      src={logoImageUrl}
                      alt={t("pages:knowledge.companyProfiles.fields.logo")}
                      className="h-full w-full object-contain"
                    />
                  ) : (
                    <ImagePlus className="h-5 w-5 text-gray-400" />
                  )}
                  {isLogoUploading ? (
                    <span className="absolute inset-0 flex items-center justify-center bg-white/70">
                      <Loader2 className="h-5 w-5 animate-spin text-gray-700" />
                    </span>
                  ) : null}
                </button>
                {logoValue ? (
                  <button
                    type="button"
                    className="pointer-events-none absolute -right-1.5 -top-1.5 z-10 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-white opacity-0 shadow-sm transition hover:pointer-events-auto hover:bg-red-600 hover:opacity-100 focus-visible:pointer-events-auto focus-visible:opacity-100 disabled:cursor-not-allowed disabled:opacity-0 peer-hover:pointer-events-auto peer-hover:opacity-100"
                    disabled={isLogoControlDisabled}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      handleLogoRemove();
                    }}
                    aria-label={t("pages:knowledge.companyProfiles.logoActions.remove")}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                ) : null}
              </div>
            </div>
            <div className="flex items-center justify-end gap-2">
              {profile ? (
                <Button type="button" onClick={openRegeneratePanel}>
                  <RefreshCw className="h-4 w-4" />
                  {t("pages:knowledge.companyProfiles.regenerate")}
                </Button>
              ) : null}
              <Button type="submit" disabled={isSaving}>
                {isSaving ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t("pages:knowledge.companyProfiles.saving")}
                  </span>
                ) : (
                  <span>
                    {t(
                      hasSavedProfile
                        ? "pages:knowledge.companyProfiles.update"
                        : "pages:knowledge.companyProfiles.confirmSave",
                    )}
                  </span>
                )}
              </Button>
            </div>
          </div>

          <Field label={t("pages:knowledge.companyProfiles.fields.name")}>
            <Input {...form.register("name")} className="h-10" />
          </Field>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field label={t("pages:knowledge.companyProfiles.fields.website")}>
              <Input {...form.register("website")} className="h-10" />
            </Field>
            <Field label={t("pages:knowledge.companyProfiles.fields.location")}>
              <Input {...form.register("location")} className="h-10" />
            </Field>
            <Field label={t("pages:knowledge.companyProfiles.fields.foundedIn")}>
              <Input {...form.register("foundedIn")} className="h-10" inputMode="numeric" />
            </Field>
            <Field label={t("pages:knowledge.companyProfiles.fields.businessType")}>
              <Input {...form.register("businessType")} className="h-10" />
            </Field>
          </div>

          <Field label={t("pages:knowledge.companyProfiles.fields.description")}>
            <Textarea {...form.register("description")} className="min-h-[96px] resize-none" />
          </Field>

          <Field label={t("pages:knowledge.companyProfiles.fields.targetAudience")}>
            <Textarea {...form.register("targetAudience")} className="min-h-[78px] resize-none" />
          </Field>

          <Field label={t("pages:knowledge.companyProfiles.fields.keyAdvantages")}>
            <Textarea
              {...form.register("sellingPointsText")}
              className="min-h-[78px] resize-none"
            />
          </Field>
        </form>
      </div>

      <Sheet open={isRegenerateOpen} onOpenChange={handleRegenerateOpenChange}>
        <SheetContent
          side="right"
          className="w-[360px] gap-0 sm:max-w-[360px]"
          onEscapeKeyDown={(event) => {
            if (isGenerating) event.preventDefault();
          }}
          onInteractOutside={(event) => {
            if (isGenerating) event.preventDefault();
          }}
        >
          <SheetHeader className="border-b px-6 py-5 text-left">
            <SheetTitle className="flex items-center gap-2 text-xl font-semibold text-gray-950">
              <RefreshCw className="h-5 w-5" />
              {t("pages:knowledge.companyProfiles.regenerateTitle")}
            </SheetTitle>
            <SheetDescription className="text-sm leading-6 text-gray-500">
              {t("pages:knowledge.companyProfiles.regenerateDescription")}
            </SheetDescription>
          </SheetHeader>
          <div className="space-y-4 px-6 py-6">
            <label
              className="block text-sm font-medium text-gray-950"
              htmlFor="company-profile-regenerate-url"
            >
              {t("pages:knowledge.companyProfiles.websiteLabel")}
            </label>
            <Input
              id="company-profile-regenerate-url"
              className="h-11"
              disabled={isGenerating}
              placeholder={t("pages:knowledge.companyProfiles.websitePlaceholder")}
              value={regenerateUrl}
              onChange={(event) => setRegenerateUrl(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  void handleRegenerate();
                }
              }}
            />
            <div className="flex justify-end">
              <Button
                type="button"
                disabled={!regenerateUrl.trim() || isGenerating}
                onClick={() => void handleRegenerate()}
              >
                {isGenerating ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t("pages:knowledge.companyProfiles.generating")}
                  </span>
                ) : (
                  <span>{t("pages:knowledge.companyProfiles.regenerate")}</span>
                )}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

interface FieldProps {
  label: string;
  children: ReactNode;
  className?: string;
}

function Field({ label, children, className = "" }: FieldProps) {
  return (
    <div className={className}>
      <label className="mb-2 block text-sm font-medium text-gray-900">{label}</label>
      {children}
    </div>
  );
}

function toFormValues(profile: WorkspaceCompanyProfile): CompanyProfileFormValues {
  return {
    logo: profile.logo,
    name: profile.name,
    website: profile.website,
    location: profile.location,
    foundedIn: profile.founded_in ? String(profile.founded_in) : "",
    businessType: profile.business_type,
    description: profile.description,
    targetAudience: profile.target_audience,
    sellingPointsText: profile.selling_points.join("\n"),
  };
}

function toSaveInput(
  values: CompanyProfileFormValues,
  profile: WorkspaceCompanyProfile | null,
): WorkspaceCompanyProfileInput {
  return {
    name: values.name,
    website: values.website,
    location: values.location,
    founded_in: parseFoundedIn(values.foundedIn),
    business_type: values.businessType,
    description: values.description,
    target_audience: values.targetAudience,
    selling_points: parseMultilineList(values.sellingPointsText),
    products: profile?.products ?? [],
    logo: values.logo,
  };
}

const ALLOWED_LOGO_TYPES = ["image/png", "image/jpeg", "image/webp"] as const;
const MAX_LOGO_SIZE_BYTES = 5 * 1024 * 1024;

function isValidLogoFile(file: File): boolean {
  return (
    ALLOWED_LOGO_TYPES.includes(file.type as (typeof ALLOWED_LOGO_TYPES)[number]) &&
    file.size <= MAX_LOGO_SIZE_BYTES
  );
}

function resolveLogoImageUrl(
  logo: string | null,
  localPreviewUrl: string | null,
  profile: WorkspaceCompanyProfile | null,
): string | null {
  if (!logo) return null;
  if (localPreviewUrl) return localPreviewUrl;
  if (profile?.logo === logo && profile.logo_url) return profile.logo_url;
  return isBrowserRenderableLogoUrl(logo) ? logo : null;
}

function isBrowserRenderableLogoUrl(value: string): boolean {
  return value.startsWith("http://") || value.startsWith("https://") || value.startsWith("data:");
}

function uploadCompanyProfileLogoToOSS(
  file: File,
  uploadUrl: string,
  formFields: Record<string, string>,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
        return;
      }
      reject(new Error(`OSS upload failed with status ${xhr.status}`));
    });
    xhr.addEventListener("error", () => {
      reject(new Error("OSS upload network error"));
    });

    const formData = new FormData();
    Object.entries(formFields).forEach(([key, value]) => {
      formData.append(key, value);
    });
    formData.append("file", file);

    xhr.open("POST", uploadUrl);
    xhr.send(formData);
  });
}

function parseFoundedIn(value: string): number | null {
  const normalized = value.trim();
  if (!normalized) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function parseMultilineList(value: string): string[] {
  const seen = new Set<string>();
  const items: string[] = [];
  for (const item of value.split(/\r?\n/)) {
    const normalized = item.trim();
    if (!normalized || seen.has(normalized)) continue;
    seen.add(normalized);
    items.push(normalized);
  }
  return items;
}
